/**
 * Os símbolos do mapa dos ensaios (onda 215).
 *
 *   «Identifique com símbolos diferentes cada tipo de sondagem, ensaio de
 *    campo, ou apenas ensaio de laboratório (quando o cliente faz a coleta
 *    e não temos o relatório de coleta shelby, deformada, etc).»
 *   «Temos que deixar os ícones mais chapados e profissionais. Segue a
 *    norma NBR 13441. Utilize apenas os símbolos de sondagem SPT, rotativa
 *    e mista dela.»                            — Humberto, 28/09/2026
 *
 * A família foi aprovada símbolo por símbolo no documento
 * `claude/214-simbolos-do-mapa-de-ensaios-rev2.html` (28/09/2026):
 *
 *   · SP e SR/SM — ABNT NBR 13441:2021, Tabela 1: programada vazada,
 *     executada cheia (SP: meio círculo; SR/SM: ponto central);
 *   · trado, CPTu, DMT, vane, permeabilidade — desenhos da casa;
 *   · bloco (cubo), tubo Shelby/Denison, saco (deformada) — a coleta;
 *   · erlenmeyer — só laboratório: amostra que a Testesolo não coletou;
 *   · CP sobre a base do molde — o corpo de prova moldado pelo cliente
 *     (onda 233; proposta B, aprovada pelo Humberto em 02/10/2026 na página
 *     `claude/217-simbolo-do-cp-moldado.html`).
 *
 * **Vazado = programada; cheio = executada/coletada.** Nas formas com
 * volume, o cheio deixa uma folga que mantém as arestas à vista. A COR não
 * diz o tipo (o desenho diz): é uma só. Coordenada herdada da obra — «por
 * aqui», não «aqui» — sai com meia opacidade, porque «vazado» passou a
 * significar «programada» e não pode mais dizer «aproximado».
 *
 * Tudo aqui é função pura, sem Leaflet e sem React: a tela desenha, o
 * teste 204 confere a classificação lendo ESTE arquivo.
 *
 * Os desenhos não usam `<mask>`: um mapa tem dezenas de marcadores, e
 * `id` repetido no documento faz a máscara de um apontar para a de outro
 * — e sumir quando o outro sai de cena. As folgas são polígonos.
 */

export type TipoSimbolo =
  | 'sp' | 'sr' | 'trado'
  | 'cptu' | 'dmt' | 'vane' | 'perm' | 'outro'
  | 'bloco' | 'tubo' | 'deform'
  | 'lab' | 'cp';

export type EstadoSimbolo = 'prog' | 'exec';

const S = 'fill="none" stroke="currentColor" stroke-width="3.5" stroke-linejoin="round" stroke-linecap="round"';
const F = 'fill="currentColor" stroke="none"';
const FS = 'fill="currentColor" stroke="currentColor" stroke-width="3.5" stroke-linejoin="round"';
const svg = (corpo: string) =>
  `<svg viewBox="0 0 60 60" aria-hidden="true" focusable="false">${corpo}</svg>`;

/* O cubo e o erlenmeyer repetem o contorno nos dois estados. */
const CUBO = `<g ${S}><polygon points="30,6 51,18 51,43 30,55 9,43 9,18"/><polyline points="9,18 30,30 51,18"/><line x1="30" y1="30" x2="30" y2="55"/></g>`;
const TUBO = `<g ${S}><ellipse cx="30" cy="12" rx="13" ry="5"/><line x1="17" y1="12" x2="17" y2="48"/><line x1="43" y1="12" x2="43" y2="48"/><path d="M17 48 A13 5 0 0 0 43 48"/></g>`;
const FRASCO = `<g ${S}><line x1="22" y1="6" x2="38" y2="6"/><path d="M26 6 V22 L13 47 Q10.5 53 17 53 H43 Q49.5 53 47 47 L34 22 V6"/></g>`;
/* O CP moldado: o cilindro em pé sobre a placa de base do molde — a placa
   é o que o separa do tubo Shelby. */
const CILINDRO_CP = `<ellipse cx="30" cy="10" rx="13" ry="4.5"/><line x1="17" y1="10" x2="17" y2="40"/><line x1="43" y1="10" x2="43" y2="40"/><path d="M17 40 A13 4.5 0 0 0 43 40"/>`;
const GOTA = 'M30 6 C30 6 13 26 13 37 A17 17 0 0 0 47 37 C47 26 30 6 30 6 Z';

const DESENHO: Record<TipoSimbolo, Record<EstadoSimbolo, string>> = {
  sp: {
    prog: svg(`<g ${S}><circle cx="30" cy="30" r="20"/><line x1="30" y1="10" x2="30" y2="50"/></g>`),
    exec: svg(`<g ${S}><circle cx="30" cy="30" r="20"/></g><path ${F} d="M30 10 A20 20 0 0 1 30 50 Z"/>`),
  },
  sr: {
    prog: svg(`<g ${S}><circle cx="30" cy="30" r="20"/><circle cx="30" cy="30" r="7"/></g>`),
    exec: svg(`<g ${S}><circle cx="30" cy="30" r="20"/></g><circle ${F} cx="30" cy="30" r="9"/>`),
  },
  trado: {
    prog: svg(`<g ${S}><line x1="17" y1="8" x2="43" y2="8"/><line x1="30" y1="8" x2="30" y2="21"/><polygon points="22,21 38,21 38,41 30,54 22,41"/><line x1="22" y1="32" x2="38" y2="26"/><line x1="22" y1="41" x2="38" y2="35"/></g>`),
    exec: svg(`<g ${S}><line x1="17" y1="8" x2="43" y2="8"/><line x1="30" y1="8" x2="30" y2="19"/></g>`
      + `<g ${F}><polygon points="20.5,19.5 39.5,19.5 39.5,23.57 20.5,30.69"/>`
      + '<polygon points="20.5,34.43 39.5,27.31 39.5,32.57 20.5,39.69"/>'
      + '<polygon points="39.5,36.31 39.5,41.5 30,55.5 21.54,43.04"/></g>'),
  },
  cptu: {
    prog: svg(`<g ${S}><rect x="13" y="7" width="34" height="11" rx="1"/><polygon points="13,18 47,18 30,53"/></g>`),
    exec: svg(`<rect ${F} x="11.25" y="5.25" width="37.5" height="11" rx="2"/><polygon ${FS} points="13,22 47,22 30,53"/>`),
  },
  dmt: {
    prog: svg(`<g ${S}><polygon points="16,6 44,6 44,41 30,54 16,41"/><circle cx="30" cy="23" r="7"/></g>`),
    exec: svg(`<path ${F} fill-rule="evenodd" d="M14.25 4.25 H45.75 V41.8 L30 56.4 L14.25 41.8 Z M40 23 A10 10 0 1 0 20 23 A10 10 0 1 0 40 23 Z"/>`
      + `<circle ${F} cx="30" cy="23" r="6.5"/>`),
  },
  vane: {
    prog: svg(`<g ${S}><line x1="30" y1="4" x2="30" y2="16"/><rect x="13" y="16" width="34" height="34" rx="1"/><line x1="30" y1="16" x2="30" y2="50"/></g>`),
    exec: svg(`<g ${S}><line x1="30" y1="4" x2="30" y2="51.75"/></g>`
      + `<rect ${F} x="11.25" y="14.25" width="15.5" height="37.5" rx="2"/>`
      + `<rect ${F} x="33.25" y="14.25" width="15.5" height="37.5" rx="2"/>`),
  },
  perm: {
    prog: svg(`<path ${S} d="${GOTA}"/>`),
    exec: svg(`<path ${FS} d="${GOTA}"/>`),
  },
  /* Placa, poço, instrumento e «outro»: fora do escopo aprovado — um
     quadrado neutro, para o registro não sumir do mapa. */
  outro: {
    prog: svg(`<rect ${S} x="15" y="15" width="30" height="30" rx="3"/>`),
    exec: svg(`<rect ${FS} x="15" y="15" width="30" height="30" rx="3"/>`),
  },
  bloco: {
    prog: svg(CUBO),
    exec: svg(`${CUBO}<g ${F}><polygon points="30,10.3 44.2,18 30,25.7 15.8,18"/>`
      + '<polygon points="13,23.8 26.5,31.8 26.5,49 13,41.2"/>'
      + '<polygon points="33.5,31.8 47,23.8 47,41.2 33.5,49"/></g>'),
  },
  tubo: {
    prog: svg(TUBO),
    exec: svg(`${TUBO}<path ${F} d="M20.5 19 A9.5 3.2 0 0 0 39.5 19 L39.5 46.5 A9.5 3.2 0 0 1 20.5 46.5 Z"/>`),
  },
  deform: {
    prog: svg(`<g ${S}><polygon points="21,6 39,6 34,15 26,15"/><path d="M26 19 C14 24 11 36 12 44 C13 51 20 53 30 53 C40 53 47 51 48 44 C49 36 46 24 34 19 Z"/></g>`),
    exec: svg(`<g ${FS}><polygon points="21,6 39,6 34,14 26,14"/><path d="M26 20 C14 25 11 36 12 44 C13 51 20 53 30 53 C40 53 47 51 48 44 C49 36 46 25 34 20 Z"/></g>`),
  },
  cp: {
    prog: svg(`<g ${S}>${CILINDRO_CP}<rect x="8" y="48" width="44" height="6" rx="1"/></g>`),
    exec: svg(`<g ${S}>${CILINDRO_CP}</g>`
      + `<path ${F} d="M20.5 15.5 A9.5 3.3 0 0 0 39.5 15.5 L39.5 39 A9.5 3.3 0 0 1 20.5 39 Z"/>`
      + `<rect ${FS} x="8" y="48" width="44" height="6" rx="1"/>`),
  },
  lab: {
    prog: svg(FRASCO),
    exec: svg(`${FRASCO}<path ${F} d="M22.5 35 H37.5 L44 47.5 Q45 49.5 42.5 49.5 H17.5 Q15 49.5 16 47.5 Z"/>`),
  },
};

/** O SVG de um símbolo, em `currentColor`. */
export function desenhoDoSimbolo(tipo: TipoSimbolo, estado: EstadoSimbolo): string {
  return DESENHO[tipo][estado];
}

/** Os grupos do filtro — que são também a legenda. Ordem da página aprovada. */
export const GRUPOS_DE_SIMBOLO: { grupo: string; tipos: { tipo: TipoSimbolo; nome: string }[] }[] = [
  { grupo: 'Sondagens', tipos: [
    { tipo: 'sp', nome: 'SPT' },
    { tipo: 'sr', nome: 'Rotativa e mista' },
    { tipo: 'trado', nome: 'Trado' },
  ] },
  { grupo: 'Ensaios de campo', tipos: [
    { tipo: 'cptu', nome: 'CPTu' },
    { tipo: 'dmt', nome: 'DMT' },
    { tipo: 'vane', nome: 'Vane' },
    { tipo: 'perm', nome: 'Permeabilidade' },
    { tipo: 'outro', nome: 'Outros pontos' },
  ] },
  { grupo: 'Coletas', tipos: [
    { tipo: 'bloco', nome: 'Bloco' },
    { tipo: 'tubo', nome: 'Shelby / Denison' },
    { tipo: 'deform', nome: 'Deformada' },
  ] },
  { grupo: 'Laboratório', tipos: [
    { tipo: 'lab', nome: 'Só laboratório' },
    { tipo: 'cp', nome: 'CP moldado pelo cliente' },
  ] },
];

export const NOME_DO_SIMBOLO: Record<TipoSimbolo, string> = Object.fromEntries(
  GRUPOS_DE_SIMBOLO.flatMap((g) => g.tipos.map((t) => [t.tipo, t.nome])),
) as Record<TipoSimbolo, string>;

export const TIPOS_DE_SIMBOLO: TipoSimbolo[] =
  GRUPOS_DE_SIMBOLO.flatMap((g) => g.tipos.map((t) => t.tipo));

/**
 * O ponto de investigação → símbolo. CPT e SCPTu usam o do CPTu; SDMT, o
 * do DMT (a diferença fica no filtro «Ensaio de campo», pelo tipo exato).
 */
const DO_PONTO: Record<string, TipoSimbolo> = {
  spt: 'sp', rotativa: 'sr', mista: 'sr', trado: 'trado',
  cpt: 'cptu', cptu: 'cptu', scptu: 'cptu', dmt: 'dmt', sdmt: 'dmt',
  vane: 'vane', permeabilidade: 'perm',
};
export function simboloDoPonto(tipo: string | null): TipoSimbolo {
  return DO_PONTO[tipo ?? ''] ?? 'outro';
}

/*
 * O acondicionamento → a coleta (vocabulário da `0014`, com a herança de
 * antes dela). O amostrador do SPT é deformada.
 */
const BLOCO = ['bloco', 'bloco_indeformado'];
const TUBO_ = ['shelby', 'denison', 'tubo_shelby'];
const SACO = ['saco_rafia', 'saco_plastico', 'spt', 'saco', 'caixa'];

/**
 * A amostra recebida → símbolo.
 *
 * Quem não é a equipe própria (`origem` ≠ `equipe_propria`: o cliente, a
 * empresa de sondagem, o controle da obra) trouxe a amostra — não há
 * relatório de coleta da Testesolo, e o que a casa fez ali foi só
 * laboratório. Da equipe própria, o acondicionamento diz a coleta; o que
 * ele não resolve (anel, frasco, «outro», vazio) cai pelo tipo:
 * indeformada → bloco, o resto → deformada.
 */
export function simboloDaAmostra(a: {
  origem: string | null; acondicionamento: string | null; tipoAmostra: string | null;
}): TipoSimbolo {
  /* O CP moldado pelo cliente tem desenho próprio, venha de quem vier (233). */
  if (a.tipoAmostra === 'cp_moldado') return 'cp';
  if (a.origem !== 'equipe_propria') return 'lab';
  const ac = a.acondicionamento ?? '';
  if (BLOCO.includes(ac)) return 'bloco';
  if (TUBO_.includes(ac)) return 'tubo';
  if (SACO.includes(ac)) return 'deform';
  return a.tipoAmostra === 'indeformada' ? 'bloco' : 'deform';
}

/**
 * A amostra prevista → símbolo (sempre vazado). A previsão só sabe se
 * será deformada ou indeformada; indeformada de campanha de sondagem é,
 * quase sempre, tubo.
 */
export function simboloDaPrevista(tipoAmostra: string | null): TipoSimbolo {
  if (tipoAmostra === 'cp_moldado') return 'cp';
  return tipoAmostra === 'indeformada' ? 'tubo' : 'deform';
}

/**
 * Os símbolos cujo cheio é «tem resultado», e não «foi coletado»: o só
 * laboratório e o CP moldado pelo cliente (233) — nos dois, a Testesolo
 * não coletou nada; o que ela entrega é o ensaio.
 */
export const SO_LABORATORIO: readonly TipoSimbolo[] = ['lab', 'cp'];

/**
 * Cheio ou vazado.
 *   · ponto: cheio quando `executado`;
 *   · coleta: a amostra recebida foi coletada — cheio;
 *   · só laboratório: cheio quando todo ensaio que vale (não cancelado)
 *     está aprovado — é quando há resultado para mostrar;
 *   · prevista: vazado, sempre.
 */
export function estadoDoPonto(situacao: string | null): EstadoSimbolo {
  return situacao === 'executado' ? 'exec' : 'prog';
}
export function estadoDoLaboratorio(status: string[]): EstadoSimbolo {
  const valem = status.filter((s) => s !== 'cancelado');
  return valem.length > 0 && valem.every((s) => s === 'aprovado') ? 'exec' : 'prog';
}

/**
 * O nome do cliente, resumido para a etiqueta de passar o mouse: sem a
 * natureza jurídica e com no máximo 24 letras, cortado na palavra.
 */
export function clienteResumido(nome: string | null | undefined): string {
  const limpo = (nome ?? '')
    .replace(/[\s,-]+(ltda|s\.?\/?a|eireli|epp|me|limitada)\.?(?=\s|$)/gi, '')
    .replace(/\s+/g, ' ').trim();
  if (limpo.length <= 24) return limpo;
  const corte = limpo.slice(0, 24);
  const espaco = corte.lastIndexOf(' ');
  return `${(espaco > 10 ? corte.slice(0, espaco) : corte).replace(/[\s,.-]+$/, '')}…`;
}
